import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { Badge, Button, Card, CardContent, confirm, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle,
  HelpCircle,
  Pencil,
  Target,
  XCircle,
} from "lucide-react";
import { useMemo } from "react";
import {
  useApprovePromotionRecordMutation,
  usePromotionLevelsLookupQuery,
  usePromotionRecordDetailQuery,
} from "../api/queries";
import {
  PromotionTour,
  promotionRecordDetailTourSteps,
  usePromotionTour,
} from "../tour";
import { PromotionRecordForm } from "../ui/PromotionRecordForm";

const backSearch = {
  offset: 0,
  limit: 20,
  sort: undefined,
  employeeId: undefined,
  periodYear: undefined,
  periodMonth: undefined,
  positionId: undefined,
  search: undefined,
};

export function PromotionRecordDetailPage() {
  const nav = useNavigate();
  const { id } = useParams({ from: "/app/hr/promotion/records/$id" });
  const detailQuery = usePromotionRecordDetailQuery(id);
  const item = detailQuery.data;
  const tour = usePromotionTour("recordDetail");
  const { has } = usePermissions();
  const canApprove = has(Permissions.hr_promotion.approve);
  const canUpdate = has(Permissions.hr_promotion.record_update);
  const approveMutation = useApprovePromotionRecordMutation();
  const levelsQuery = usePromotionLevelsLookupQuery();

  const levelOptions = useMemo(() => {
    const list = (levelsQuery.data ?? []).map((l) => ({
      value: l.id,
      label: l.label,
    }));
    if (item) {
      const seen = new Set(list.map((o) => o.value));
      const extra: { value: string; label: string }[] = [];
      if (item.fromLevelId && !seen.has(item.fromLevelId)) {
        extra.push({
          value: item.fromLevelId,
          label: item.fromLevelName ?? item.fromLevelId,
        });
        seen.add(item.fromLevelId);
      }
      if (item.toLevelId && !seen.has(item.toLevelId)) {
        extra.push({
          value: item.toLevelId,
          label: item.toLevelName ?? item.toLevelId,
        });
      }
      return [...extra, ...list];
    }
    return list;
  }, [
    levelsQuery.data,
    item?.fromLevelId,
    item?.fromLevelName,
    item?.toLevelId,
    item?.toLevelName,
  ]);

  const back = () =>
    nav({
      to: "/app/hr/promotion/records",
      search: backSearch,
    });

  const handleApprove = async () => {
    if (!id || !item) return;
    const ok = await confirm({
      title: "ຢືນຢັນການອະນຸມັດ",
      description: `ຢືນຢັນອະນຸມັດການເລື່ອນຂັ້ນຂອງ ${item.employeeName} ບໍ?`,
      actionText: "ອະນຸມັດ",
    });
    if (!ok) return;
    approveMutation.mutate(id, {
      onSuccess: () => toast.success("ອະນຸມັດບັນທຶກການເຕີບໂຕແລ້ວ"),
      onError: (err) => toast.error(err?.message ?? "ອະນຸມັດບໍ່ສຳເລັດ"),
    });
  };

  return (
    <>
      <Header />
      <Main>
        <PromotionTour
          steps={promotionRecordDetailTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="promotion-record-detail-back"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">
                ລາຍລະອຽດບັນທຶກການເລື່ອນຕຳແໜ່ງ
              </h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
            </div>
            {item && (
              <p className="text-muted-foreground text-sm">
                {item.employeeName}
                {item.promotedAt && (
                  <>
                    {" · "}
                    {new Date(item.promotedAt).toLocaleDateString("lo-LA", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={back} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
            {item && (
              <>
                {item.approvedBy != null ? (
                  <Badge
                    variant="default"
                    className="bg-emerald-600 text-white"
                  >
                    ອະນຸມັດແລ້ວ
                  </Badge>
                ) : (
                  <Badge variant="secondary">ລໍຖ້າອະນຸມັດ</Badge>
                )}
                {canApprove && item.approvedBy == null && (
                  <Button
                    size="sm"
                    onClick={handleApprove}
                    disabled={
                      approveMutation.isPending ||
                      (item.kpiCheckMode === "mandatory" &&
                        item.kpiCheck != null &&
                        !item.kpiCheck.passed)
                    }
                    className="gap-1.5"
                    title={
                      item.kpiCheckMode === "mandatory" &&
                      item.kpiCheck != null &&
                      !item.kpiCheck.passed
                        ? "ບໍ່ສາມາດອະນຸມັດເພາະ KPI ຍັງບໍ່ຜ່ານທຸກລາຍການ"
                        : undefined
                    }
                  >
                    <CheckCircle className="h-4 w-4" />
                    ອະນຸມັດ
                  </Button>
                )}
                {canUpdate && item.approvedBy == null && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      id &&
                      nav({
                        to: "/app/hr/promotion/records/$id/edit",
                        params: { id },
                        search: backSearch,
                      })
                    }
                    className="gap-1.5"
                  >
                    <Pencil className="h-4 w-4" />
                    ແກ້ໄຂ
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {detailQuery.isLoading && (
          <div className="mx-auto max-w-3xl">
            <Card>
              <CardContent className="py-12">
                <p className="text-center text-muted-foreground text-sm">
                  ກຳລັງໂຫຼດ...
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {!detailQuery.isLoading && !item && (
          <div className="mx-auto max-w-3xl">
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground text-sm">ບໍ່ພົບບັນທຶກການເຕີບໂຕ</p>
                <Button variant="outline" onClick={back} className="mt-4 gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  ກັບໄປລາຍການ
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {!detailQuery.isLoading && item && (
          <div
            className="mx-auto max-w-3xl space-y-6"
            data-tourid="promotion-record-detail-content"
          >
            {item.kpiCheck != null && (
              <Card>
                <CardContent>
                  <div className="flex items-center gap-2 font-medium text-sm">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    ສະຖານະ KPI (ໄລຍະລ່າສຸດ)
                    {item.kpiCheck.passed ? (
                      <Badge variant="default" className="bg-emerald-600">
                        ຜ່ານທຸກລາຍການ
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        ຍັງບໍ່ຜ່ານ
                      </Badge>
                    )}
                    {item.kpiCheckMode === "mandatory" &&
                      !item.kpiCheck.passed && (
                        <span className="text-muted-foreground text-xs">
                          (ຕ້ອງຜ່ານທຸກລາຍການຈຶ່ງອະນຸມັດໄດ້)
                        </span>
                      )}
                  </div>
                  {item.kpiCheck.details.length > 0 && (
                    <ul className="mt-3 space-y-1 border-t pt-3 text-muted-foreground text-xs">
                      {item.kpiCheck.details.map((d) => (
                        <li
                          key={d.assignmentId}
                          className="flex items-center gap-2"
                        >
                          <span className="font-medium text-foreground">
                            {d.kpiName}
                          </span>
                          <span>
                            {d.periodYear}
                            {d.periodMonth != null ? `/${d.periodMonth}` : ""}
                            {d.periodQuarter != null
                              ? ` Q${d.periodQuarter}`
                              : ""}
                          </span>
                          <Badge
                            variant={
                              d.status === "pass"
                                ? "default"
                                : d.status === "fail"
                                  ? "destructive"
                                  : "secondary"
                            }
                            className="text-xs"
                          >
                            {d.status === "pass"
                              ? "ຜ່ານ"
                              : d.status === "fail"
                                ? "ບໍ່ຜ່ານ"
                                : d.status === "pending"
                                  ? "ລໍຖ້າ"
                                  : "ບໍ່ມີຜົນ"}
                          </Badge>
                          {d.actualValue != null && (
                            <span>
                              ຜົນ: {d.actualValue}
                              {d.targetValue != null
                                ? ` / ເປົ້າໝາຍ: ${d.targetValue}`
                                : ""}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )}
            <PromotionRecordForm
              mode="view"
              initialValues={item}
              onCancel={back}
              levelOptions={levelOptions}
            />
          </div>
        )}
      </Main>
    </>
  );
}
